import React, { useState, useRef, useEffect, useTransition, useMemo, memo } from 'react';
import ReactDOM from 'react-dom';
import MobileInput from './MobileInput';
import ActionButtons from '../../ui/ActionButtons/ActionButtons';
import Tooltip from '../../ui/Tooltip/Tooltip';
import Checkbox from '../../ui/Checkbox/Checkbox';
import './mobile-dropdown.css';

/**
 * Memoized Dropdown Item for performance
 */
const DropdownItem = memo(({ opt, isActive, multiple, onSelect }) => {
    const optValue = Array.isArray(opt) ? opt[0] : (opt.id ?? opt.value ?? opt.product_id);
    const optLabel = Array.isArray(opt) ? opt[1] : (opt.name ?? opt.label ?? opt.text);

    return (
        <button
            type="button"
            className={`mobile-dropdown-item ${isActive ? 'active' : ''} ${multiple ? 'is-multiple' : ''}`}
            onClick={() => onSelect(optValue)}
        >
            {multiple ? (
                <div className="mobile-dropdown-item-checkbox-wrapper">
                    <Checkbox
                        checked={isActive}
                        onChange={() => { }}
                        label={optLabel}
                        checkSize="18px"
                        fontSize="14px"
                        align="center"
                    />
                </div>
            ) : (
                <>
                    <span className='mobile-dropdown-item-text'>{optLabel}</span>
                    {isActive && <i className="bi bi-check2"></i>}
                </>
            )}
        </button>
    );
});

/**
 * MobileDropdown Component
 * A premium custom dropdown for mobile views.
 */
const MobileDropdown = ({
    label,
    options = [],
    value,
    onChange,
    placeholder = "Selecciona...",
    icon,
    error,
    name: inputName,
    searchable = false,
    onSearch,
    clearable = false,
    disabled = false,
    multiple = false,
    actions = [],
    enableSelectAll = false,
    selectAllLabel = "Seleccionar todos"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [coords, setCoords] = useState(null);
    const [openUpwards, setOpenUpwards] = useState(false);
    const [isPending, startTransition] = useTransition();

    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const searchInputRef = useRef(null);

    // Update position when opening
    useEffect(() => {
        if (!isOpen) {
            setSearchQuery("");
            setCoords(null);
        }
    }, [isOpen]);

    const handleToggle = () => {
        if (disabled) return;
        if (!isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const shouldOpenUpwards = spaceBelow < 350 && rect.top > spaceBelow;

            setCoords({
                top: rect.top + window.scrollY,
                bottom: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
            setOpenUpwards(shouldOpenUpwards);
        }
        setIsOpen(!isOpen);
    };

    const filteredOptions = useMemo(() => {
        if (!searchQuery) return options;
        const query = searchQuery.toLowerCase();
        return options.filter(opt => {
            const label = Array.isArray(opt) ? opt[1] : (opt.name || opt.label || opt.text);
            return String(label).toLowerCase().includes(query);
        });
    }, [options, searchQuery]);

    const handleSelect = (optionValue) => {
        if (disabled) return;

        startTransition(() => {
            if (multiple) {
                const currentValues = Array.isArray(value) ? value : [];
                const isSelected = currentValues.includes(optionValue);
                let nextValues;

                if (isSelected) {
                    nextValues = currentValues.filter(v => v !== optionValue);
                } else {
                    nextValues = [...currentValues, optionValue];
                }

                onChange(nextValues);
            } else {
                onChange(optionValue);
                setIsOpen(false);
            }
        });
    };

    const handleSelectAll = (e) => {
        if (e) e.stopPropagation();
        if (disabled || !multiple) return;

        startTransition(() => {
            const currentValues = Array.isArray(value) ? value : [];
            const filteredValues = filteredOptions.map(opt =>
                Array.isArray(opt) ? opt[0] : (opt.id ?? opt.value ?? opt.product_id)
            );

            const allInFilteredSelected = filteredValues.every(v => currentValues.includes(v));

            let nextValues;
            if (allInFilteredSelected) {
                nextValues = currentValues.filter(v => !filteredValues.includes(v));
            } else {
                nextValues = [...new Set([...currentValues, ...filteredValues])];
            }

            onChange(nextValues);
        });
    };

    const handleClear = (e) => {
        if (e) {
            e.stopPropagation();
            if (e.preventDefault) e.preventDefault();
        }
        if (disabled) return;
        startTransition(() => {
            onChange(multiple ? [] : null);
            if (onSearch) onSearch("");
        });
        setIsOpen(false);
    };

    const getSelectedLabel = () => {
        if (multiple) {
            const currentValues = Array.isArray(value) ? value : [];
            if (currentValues.length === 0) return placeholder;

            if (currentValues.length > 2) return `${currentValues.length} seleccionados`;

            const selectedLabels = options
                .filter(opt => {
                    const optValue = Array.isArray(opt) ? opt[0] : (opt.id ?? opt.value ?? opt.product_id);
                    return currentValues.includes(optValue);
                })
                .map(opt => Array.isArray(opt) ? opt[1] : (opt.name || opt.label || opt.text));

            if (selectedLabels.length === 0) return placeholder;
            return selectedLabels.join(", ");
        }

        if (value === null || value === undefined) return placeholder;

        const found = options.find(opt => {
            const optValue = Array.isArray(opt) ? opt[0] : (opt.id ?? opt.value ?? opt.product_id);
            return optValue === value;
        });

        if (!found) return placeholder;
        if (Array.isArray(found)) return found[1];
        return found.name || found.label || found.text;
    };

    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            setTimeout(() => searchInputRef.current.focus(), 50);
        }
    }, [isOpen, searchable]);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                const menu = document.querySelector('.mobile-dropdown-menu-portal');
                if (menu && menu.contains(event.target)) return;
                setIsOpen(false);
            }
        };

        const handleScroll = (event) => {
            const menu = document.querySelector('.mobile-dropdown-menu-portal');
            if (menu && menu.contains(event.target)) return;

            if (triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();

                // Si el input se sale por completo de la ventana, lo cerramos
                if (rect.bottom < 0 || rect.top > window.innerHeight) {
                    setIsOpen(false);
                    return;
                }

                const spaceBelow = window.innerHeight - rect.bottom;
                const shouldOpenUpwards = spaceBelow < 350 && rect.top > spaceBelow;

                setCoords({
                    top: rect.top + window.scrollY,
                    bottom: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width
                });
                setOpenUpwards(shouldOpenUpwards);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScroll, true);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [isOpen]);

    const isAllSelected = useMemo(() => {
        if (!multiple || !enableSelectAll || filteredOptions.length === 0) return false;
        const currentValues = Array.isArray(value) ? value : [];
        return filteredOptions.every(opt => {
            const v = Array.isArray(opt) ? opt[0] : (opt.id ?? opt.value ?? opt.product_id);
            return currentValues.includes(v);
        });
    }, [multiple, enableSelectAll, filteredOptions, value]);

    return (
        <div className="mobile-dropdown-container" ref={dropdownRef} name={inputName}>
            {label && <label className="mobile-dropdown-label">{label}</label>}

            <div className={`d-flex align-items-center gap-2 ${isPending ? 'opacity-75' : ''}`}>
                <div
                    ref={triggerRef}
                    className={`mobile-dropdown-wrapper flex-grow-1 ${isOpen ? 'is-open' : ''} ${error ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''}`}
                >
                    <button
                        type="button"
                        className="mobile-dropdown-toggle"
                        onClick={handleToggle}
                        disabled={disabled}
                    >
                        <div className="d-flex align-items-center justify-content-between w-100 gap-3">
                            <span className={`mobile-dropdown-selected-text ${(multiple ? (!value || value.length === 0) : (value === null || value === undefined)) ? 'is-placeholder' : ''}`}>
                                {getSelectedLabel()}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                                {isPending && (
                                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                )}
                                {icon && !(clearable && (multiple ? (value?.length > 0) : (value !== null && value !== undefined))) && (
                                    <span className="mobile-dropdown-icon-right">
                                        {typeof icon === 'string' ? <i className={`bi ${icon}`}></i> : icon}
                                    </span>
                                )}
                                <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} mobile-dropdown-chevron`}></i>
                            </div>
                        </div>
                    </button>

                    {clearable && (multiple ? (value?.length > 0) : (value !== null && value !== undefined)) && (
                        <div
                            className="mobile-dropdown-clear-button"
                            onClick={handleClear}
                            onMouseDown={(e) => e.stopPropagation()}
                        >
                            <Tooltip text="Limpiar" position='top'>
                                <i className="bi bi-x-lg"></i>
                            </Tooltip>
                        </div>
                    )}

                    {isOpen && coords && ReactDOM.createPortal(
                        <div
                            className={`mobile-dropdown-menu mobile-dropdown-menu-portal ${openUpwards ? 'is-upwards' : ''}`}
                            style={{
                                position: 'absolute',
                                top: openUpwards ? `${coords.top - 5}px` : `${coords.bottom + 5}px`,
                                left: `${coords.left}px`,
                                width: `${coords.width}px`,
                                transform: openUpwards ? 'translateY(-100%)' : 'none',
                                zIndex: 9999
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {searchable && (
                                <div className="mobile-dropdown-search-wrapper" style={{ padding: '8px 12px' }}>
                                    <MobileInput
                                        ref={searchInputRef}
                                        type="search"
                                        variant="search"
                                        placeholder="Buscar..."
                                        value={searchQuery}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSearchQuery(val);
                                            if (onSearch) onSearch(val);
                                        }}
                                        icon={<i className="bi bi-search"></i>}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            )}

                            {multiple && enableSelectAll && filteredOptions.length > 0 && (
                                <button
                                    type="button"
                                    className={`mobile-dropdown-item select-all-item ${isAllSelected ? 'active' : ''}`}
                                    onClick={handleSelectAll}
                                >
                                    <div className="mobile-dropdown-item-checkbox-wrapper">
                                        <Checkbox
                                            checked={isAllSelected}
                                            onChange={() => { }}
                                            label={selectAllLabel}
                                            checkSize="18px"
                                            fontSize="14px"
                                            align="center"
                                            bold={true}
                                        />
                                    </div>
                                </button>
                            )}

                            <div className="mobile-dropdown-items-list">
                                {filteredOptions.length > 0 ? (
                                    filteredOptions.map((opt, index) => {
                                        const optValue = Array.isArray(opt) ? opt[0] : (opt.id ?? opt.value ?? opt.product_id);
                                        const isActive = multiple
                                            ? (Array.isArray(value) && value.includes(optValue))
                                            : optValue === value;

                                        return (
                                            <DropdownItem
                                                key={optValue || index}
                                                opt={opt}
                                                isActive={isActive}
                                                multiple={multiple}
                                                onSelect={handleSelect}
                                            />
                                        );
                                    })
                                ) : (
                                    <div className="mobile-dropdown-no-results">
                                        No se encontraron resultados
                                    </div>
                                )}
                            </div>
                        </div>,
                        document.body
                    )}
                </div>

                {actions && actions.length > 0 && (
                    <ActionButtons actions={actions} />
                )}
            </div>
            {error && <span className="mobile-dropdown-error-msg">{error}</span>}
        </div>
    );
};

export default MobileDropdown;
